import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowDown, ShieldCheck } from "lucide-react";
import { scrollToId } from "@/components/Navbar";

const HERO_IMG =
  "https://images.unsplash.com/photo-1730742298439-6d82f9edc3c2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBhdXRvbW90aXZlJTIwc3BvcnRzJTIwY2FyJTIwY29ja3BpdCUyMG5pZ2h0JTIwaGVhZGxpZ2h0cyUyMGhlcm98ZW58MHx8fHwxNzkyMTQ3NTQyfDA&ixlib=rb-4.1.0&q=85";
const CAM_IMG = "/gallery/blackvue-stealth.webp";

const KineticLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden">
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
        <img src={HERO_IMG} alt="Performance car at night" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-obsidian via-obsidian/80 to-obsidian/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-transparent to-obsidian/60" />
        <div className="absolute inset-0 bg-[radial-gradient(#1f2937_1px,transparent_1px)] [background-size:26px_26px] opacity-30" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 w-full pt-28 pb-20">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.35em] text-sky-500/90 mb-6 flex items-center gap-3"
          data-testid="hero-eyebrow"
        >
          <span className="w-2 h-2 rounded-full bg-sky-500 animate-pulse-dot" />
          Dash Cam Supply &amp; Hardwire Installation — Perth Metro
        </motion.p>

        <h1 className="font-display font-black uppercase leading-[0.9] tracking-tight text-[15vw] sm:text-[11vw] lg:text-[7.5rem]">
          <KineticLine delay={0.35}>
            <span className="text-gray-50">Every drive.</span>
          </KineticLine>
          <KineticLine delay={0.5}>
            <span className="text-gray-50">
              On <span className="text-sky-500 italic">record.</span>
            </span>
          </KineticLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.7 }}
          className="mt-7 max-w-xl text-sm sm:text-base text-gray-300 leading-relaxed"
          data-testid="hero-subtext"
        >
          Factory-clean dash camera installation with 100% concealed wiring and 24/7 parking-mode
          security. Mobile service across the entire Perth metro — your warranty, trim and
          interior stay untouched.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.7 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-quote-btn"
            onClick={() => scrollToId("contact")}
            className="bg-sky-500 text-obsidian font-display font-bold uppercase tracking-widest text-base px-8 py-4 rounded-md hover:bg-sky-400 hover:shadow-[0_0_30px_rgba(14,165,233,0.45)] hover:-translate-y-0.5 transition-[background-color,box-shadow,transform] duration-300"
          >
            Get a Free Quote
          </button>
          <a
            data-testid="hero-call-btn"
            href="tel:0415414296"
            className="flex items-center gap-3 border border-white/20 text-gray-100 font-display font-bold uppercase tracking-widest text-base px-8 py-4 rounded-md hover:border-sky-500/60 hover:text-sky-400 transition-[border-color,color] duration-300"
          >
            <Phone size={16} className="text-sky-500" />
            0415 414 296
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.25, duration: 0.8 }}
          className="mt-14 flex flex-wrap gap-x-10 gap-y-4 font-mono text-[10px] sm:text-xs uppercase tracking-[0.25em] text-gray-400"
          data-testid="hero-stats"
        >
          <span><span className="text-sky-500">500+</span> installs completed</span>
          <span><span className="text-sky-500">12-mo</span> workmanship warranty</span>
          <span className="flex items-center gap-2"><ShieldCheck size={14} className="text-sky-500" /> No battery drain guarantee</span>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, x: 60 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 1.1, duration: 1, ease: [0.22, 1, 0.36, 1] }}
        className="hidden xl:block absolute right-12 bottom-16 z-10 w-72"
      >
        <div className="relative rounded-lg overflow-hidden border border-sky-500/30 shadow-[0_0_40px_rgba(14,165,233,0.15)]" style={{ clipPath: "polygon(0 0, 100% 0, 100% 88%, 88% 100%, 0 100%)" }}>
          <img src={CAM_IMG} alt="Dash camera mounted behind rearview mirror" className="w-full h-44 object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-obsidian/90 to-transparent" />
          <p className="absolute bottom-3 left-4 font-mono text-[10px] uppercase tracking-[0.25em] text-sky-400">
            Stealth mount / zero visible wire
          </p>
        </div>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollToId("services")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 text-gray-500 hover:text-sky-400 transition-colors"
        aria-label="Scroll to services"
      >
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }} className="block">
          <ArrowDown size={20} />
        </motion.span>
      </motion.button>
    </section>
  );
};

export default Hero;
